import type { PersonRow, PersonView } from "@/features/people/schemas";
import type { CompanyModule } from "@/shared/modules";

type Viewer = PersonRow["role"];

/**
 * The same people who may send invitations: `/api/invitations` refuses everyone else, so the People
 * table must not become a second way to read an address nobody has agreed to share.
 */
export function seesInvitations(viewer: Viewer): boolean {
  return viewer === "admin" || viewer === "manager";
}

/** Admins and Managers run the work: Team's tabs, a person's hours and leave, reassigning. */
export function runsTeam(viewer: Viewer): boolean {
  return viewer === "admin" || viewer === "manager";
}

/**
 * Presence is workforce data and in no spec, so only the Admin sees it (founder, 20 Oct 2026). The
 * repository and the table both read this.
 */
export function seesLastSeen(viewer: Viewer): boolean {
  return viewer === "admin";
}

/**
 * Admin only, never your own page, never the last active admin: a company with no admin is
 * unreachable. Absent rather than disabled; the pill beside it already says who holds the company.
 */
export function mayChangeAccessOf(
  viewer: { role: Viewer; email: string },
  subject: PersonRow,
  everyone: readonly PersonRow[],
): boolean {
  if (viewer.role !== "admin") return false;
  if (subject.kind !== "person") return false;
  if (subject.email === viewer.email) return false;
  if (subject.role !== "admin" || subject.state !== "active") return true;
  const liveAdmins = everyone.filter(
    (row) =>
      row.kind === "person" && row.role === "admin" && row.state === "active",
  );
  return liveAdmins.length > 1;
}

/**
 * The tabs of a person's page the viewer may open. Someone's hours and leave are theirs and their
 * managers'; pay is the Admin's alone; a module that is off has no tab.
 */
export function personViewsFor(
  viewer: Viewer,
  own: boolean,
  modules: readonly CompanyModule[],
): PersonView[] {
  const managed = own || runsTeam(viewer);
  return [
    "tasks" as const,
    ...(managed && modules.includes("attendance")
      ? ["attendance" as const]
      : []),
    ...(managed && modules.includes("leave") ? ["leave" as const] : []),
    ...(viewer === "admin" && modules.includes("payslips")
      ? ["pay" as const]
      : []),
  ];
}
